import { IDesignSession } from '../../../Models/IDesignSession';
import { IMultiSectionUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { IMultiSectionProperties } from '../../../Models/IMultiSectionProperties';
import { IRangeValidation } from '../../../Models/IRangeValidation';
import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { getLocalStorageItem } from '../../../Services/Global';

// OPTIMIZED: Consolidated state interface for useReducer
export interface ProductDesignFormState {
  // Core data
  designSession: IDesignSession[];
  unitConfiguration?: IMultiSectionUnitConfiguration;
  amountOfSections: number;
  section: IMultiSectionUnitConfiguration;
  sectionCollection: IMultiSectionUnitConfiguration[];
  userInputs?: IMultiSectionProperties[];
  rangeValidations?: IRangeValidation[];
  
  // Media inputs
  coldMediaFlowrate?: number;
  coldMediaTempIn?: number;
  coldMediaTempOut?: number;
  coldMediaPressureDrop?: number;
  hotMediaTempIn?: number;
  hotMediaTempOut?: number;
  hotMediaPressureDrop?: number;
  hotMediaFlowrate?: number;
  
  // UI states
  responseReceived: boolean;
  haveColdMediaOutputs: boolean;
  haveHotMediaOutputs: boolean;
  designReceived: boolean;
  formHasErrors: boolean;
  pageland: boolean;
  initialLoading: boolean;
  isLoading: boolean;
  isXmlUsable: boolean;
  showXmlMessage: boolean;
  xmlMessage: string;
  firstInteraction?: Date;
  submitText: string;
  disableSubmit: boolean;
  iosContent: string;
  showWarningForPHE: boolean;
  disableCopyButton: boolean;
  showAdditionalSteps: boolean;
  isCopyLoading: boolean;
  xmlContentReady: boolean;
  
  // Error states
  warningMessage?: JSX.Element;
  hotMediaFlowrateError: string;
  hotMediaTempInError: string;
  hotMediaTempOutError: string;
  temperatureError: string[]; // Changed from string to string[] to handle multiple errors
  coldMediaFlowrateError: string;
  coldMediaTempInError: string;
  coldMediaTempOutError: string;
  coldMediaPressureDropError: string;
  hotMediaPressureDropError: string;
  
  // Additional states
  pheTypes: IPHEPlateType[];
  flowUnit: string;
  readOnlyfields: string[];
  showWarningDialog: boolean;
  showInformation: boolean;
  inputValues: { [key: string]: any };
}

// OPTIMIZED: Action types for useReducer
export type ProductDesignFormAction = 
  | { type: 'SET_DESIGN_SESSION'; payload: IDesignSession[] }
  | { type: 'SET_UNIT_CONFIGURATION'; payload: IMultiSectionUnitConfiguration }
  | { type: 'SET_AMOUNT_OF_SECTIONS'; payload: number }
  | { type: 'SET_SECTION'; payload: IMultiSectionUnitConfiguration }
  | { type: 'SET_SECTION_COLLECTION'; payload: IMultiSectionUnitConfiguration[] }
  | { type: 'SET_USER_INPUTS'; payload: IMultiSectionProperties[] }
  | { type: 'SET_RANGE_VALIDATIONS'; payload: IRangeValidation[] }
  | { type: 'SET_COLD_MEDIA_FLOWRATE'; payload?: number }
  | { type: 'SET_COLD_MEDIA_TEMP_IN'; payload?: number }
  | { type: 'SET_COLD_MEDIA_TEMP_OUT'; payload?: number }
  | { type: 'SET_COLD_MEDIA_PRESSURE_DROP'; payload?: number }
  | { type: 'SET_HOT_MEDIA_TEMP_IN'; payload?: number }
  | { type: 'SET_HOT_MEDIA_TEMP_OUT'; payload?: number }
  | { type: 'SET_HOT_MEDIA_PRESSURE_DROP'; payload?: number }
  | { type: 'SET_HOT_MEDIA_FLOWRATE'; payload?: number }
  | { type: 'SET_RESPONSE_RECEIVED'; payload: boolean }
  | { type: 'SET_HAVE_COLD_MEDIA_OUTPUTS'; payload: boolean }
  | { type: 'SET_HAVE_HOT_MEDIA_OUTPUTS'; payload: boolean }
  | { type: 'SET_DESIGN_RECEIVED'; payload: boolean }
  | { type: 'SET_FORM_HAS_ERRORS'; payload: boolean }
  | { type: 'SET_PAGELAND'; payload: boolean }
  | { type: 'SET_INITIAL_LOADING'; payload: boolean }
  | { type: 'SET_IS_LOADING'; payload: boolean }
  | { type: 'SET_IS_XML_USABLE'; payload: boolean }
  | { type: 'SET_SHOW_XML_MESSAGE'; payload: boolean }
  | { type: 'SET_XML_MESSAGE'; payload: string }
  | { type: 'SET_FIRST_INTERACTION'; payload?: Date }
  | { type: 'SET_SUBMIT_TEXT'; payload: string }
  | { type: 'SET_DISABLE_SUBMIT'; payload: boolean }
  | { type: 'SET_IOS_CONTENT'; payload: string }
  | { type: 'SET_SHOW_WARNING_FOR_PHE'; payload: boolean }
  | { type: 'SET_DISABLE_COPY_BUTTON'; payload: boolean }
  | { type: 'SET_SHOW_ADDITIONAL_STEPS'; payload: boolean }
  | { type: 'SET_IS_COPY_LOADING'; payload: boolean }
  | { type: 'SET_XML_CONTENT_READY'; payload: boolean }
  | { type: 'SET_WARNING_MESSAGE'; payload?: JSX.Element }
  | { type: 'SET_HOT_MEDIA_FLOWRATE_ERROR'; payload: string }
  | { type: 'SET_HOT_MEDIA_TEMP_IN_ERROR'; payload: string }
  | { type: 'SET_HOT_MEDIA_TEMP_OUT_ERROR'; payload: string }
  | { type: 'SET_TEMPERATURE_ERROR'; payload: string[] }
  | { type: 'SET_COLD_MEDIA_FLOWRATE_ERROR'; payload: string }
  | { type: 'SET_COLD_MEDIA_TEMP_IN_ERROR'; payload: string }
  | { type: 'SET_COLD_MEDIA_TEMP_OUT_ERROR'; payload: string }
  | { type: 'SET_COLD_MEDIA_PRESSURE_DROP_ERROR'; payload: string }
  | { type: 'SET_HOT_MEDIA_PRESSURE_DROP_ERROR'; payload: string }
  | { type: 'SET_PHE_TYPES'; payload: IPHEPlateType[] }
  | { type: 'SET_FLOW_UNIT'; payload: string }
  | { type: 'SET_READ_ONLY_FIELDS'; payload: string[] }
  | { type: 'SET_SHOW_WARNING_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_INFORMATION'; payload: boolean }
  | { type: 'SET_INPUT_VALUES'; payload: { [key: string]: any } }
  | { type: 'RESET_ERRORS' }
  | { type: 'RESET_FORM' }
  | { type: 'RESET_FORM_VALUES' };

// OPTIMIZED: Initial state for useReducer
export const initialState: ProductDesignFormState = {
  designSession: [],
  amountOfSections: 1,
  section: {},
  sectionCollection: [{ sectionNumber: 1 }],
  responseReceived: false,
  haveColdMediaOutputs: false,
  haveHotMediaOutputs: false,
  designReceived: false,
  formHasErrors: false,
  pageland: false,
  initialLoading: true,
  isLoading: false,
  isXmlUsable: false,
  showXmlMessage: false,
  xmlMessage: 'This is the initial message\nnew line',
  submitText: 'Calculate',
  disableSubmit: true,
  iosContent: '',
  showWarningForPHE: false,
  disableCopyButton: false,
  showAdditionalSteps: false,
  isCopyLoading: false,
  xmlContentReady: false,
  hotMediaFlowrateError: '',
  hotMediaTempInError: '',
  hotMediaTempOutError: '',
  temperatureError: [],
  coldMediaFlowrateError: '',
  coldMediaTempInError: '',
  coldMediaTempOutError: '',
  coldMediaPressureDropError: '',
  hotMediaPressureDropError: '',
  pheTypes: [],
  flowUnit: '',
  readOnlyfields: [],
  showWarningDialog: false,
  showInformation: true,
  inputValues: {}
};

// OPTIMIZED: Reducer function to handle all state updates
export const productDesignFormReducer = (state: ProductDesignFormState, action: ProductDesignFormAction): ProductDesignFormState => {
  switch (action.type) {
    case 'SET_DESIGN_SESSION':
      return { ...state, designSession: action.payload };
    case 'SET_UNIT_CONFIGURATION':
      return { ...state, unitConfiguration: action.payload };
    case 'SET_AMOUNT_OF_SECTIONS':
      return { ...state, amountOfSections: action.payload };
    case 'SET_SECTION':
      return { ...state, section: action.payload };
    case 'SET_SECTION_COLLECTION':
      return { ...state, sectionCollection: action.payload };
    case 'SET_USER_INPUTS':
      return { ...state, userInputs: action.payload };
    case 'SET_RANGE_VALIDATIONS':
      return { ...state, rangeValidations: action.payload };
    case 'SET_COLD_MEDIA_FLOWRATE':
      return { ...state, coldMediaFlowrate: action.payload };
    case 'SET_COLD_MEDIA_TEMP_IN':
      return { ...state, coldMediaTempIn: action.payload };
    case 'SET_COLD_MEDIA_TEMP_OUT':
      return { ...state, coldMediaTempOut: action.payload };
    case 'SET_COLD_MEDIA_PRESSURE_DROP':
      return { ...state, coldMediaPressureDrop: action.payload };
    case 'SET_HOT_MEDIA_TEMP_IN':
      return { ...state, hotMediaTempIn: action.payload };
    case 'SET_HOT_MEDIA_TEMP_OUT':
      return { ...state, hotMediaTempOut: action.payload };
    case 'SET_HOT_MEDIA_PRESSURE_DROP':
      return { ...state, hotMediaPressureDrop: action.payload };
    case 'SET_HOT_MEDIA_FLOWRATE':
      return { ...state, hotMediaFlowrate: action.payload };
    case 'SET_RESPONSE_RECEIVED':
      return { ...state, responseReceived: action.payload };
    case 'SET_HAVE_COLD_MEDIA_OUTPUTS':
      return { ...state, haveColdMediaOutputs: action.payload };
    case 'SET_HAVE_HOT_MEDIA_OUTPUTS':
      return { ...state, haveHotMediaOutputs: action.payload };
    case 'SET_DESIGN_RECEIVED':
      return { ...state, designReceived: action.payload };
    case 'SET_FORM_HAS_ERRORS':
      return { ...state, formHasErrors: action.payload };
    case 'SET_PAGELAND':
      return { ...state, pageland: action.payload };
    case 'SET_INITIAL_LOADING':
      return { ...state, initialLoading: action.payload };
    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_IS_XML_USABLE':
      return { ...state, isXmlUsable: action.payload };
    case 'SET_SHOW_XML_MESSAGE':
      return { ...state, showXmlMessage: action.payload };
    case 'SET_XML_MESSAGE':
      return { ...state, xmlMessage: action.payload };
    case 'SET_FIRST_INTERACTION':
      return { ...state, firstInteraction: action.payload };
    case 'SET_SUBMIT_TEXT':
      return { ...state, submitText: action.payload };
    case 'SET_DISABLE_SUBMIT':
      return { ...state, disableSubmit: action.payload };
    case 'SET_IOS_CONTENT':
      return { ...state, iosContent: action.payload };
    case 'SET_SHOW_WARNING_FOR_PHE':
      return { ...state, showWarningForPHE: action.payload };
    case 'SET_DISABLE_COPY_BUTTON':
      return { ...state, disableCopyButton: action.payload };
    case 'SET_SHOW_ADDITIONAL_STEPS':
      return { ...state, showAdditionalSteps: action.payload };
    case 'SET_IS_COPY_LOADING':
      return { ...state, isCopyLoading: action.payload };
    case 'SET_XML_CONTENT_READY':
      return { ...state, xmlContentReady: action.payload };
    case 'SET_WARNING_MESSAGE':
      return { ...state, warningMessage: action.payload };
    case 'SET_HOT_MEDIA_FLOWRATE_ERROR':
      return { ...state, hotMediaFlowrateError: action.payload };
    case 'SET_HOT_MEDIA_TEMP_IN_ERROR':
      return { ...state, hotMediaTempInError: action.payload };
    case 'SET_HOT_MEDIA_TEMP_OUT_ERROR':
      return { ...state, hotMediaTempOutError: action.payload };
    case 'SET_TEMPERATURE_ERROR':
      return { ...state, temperatureError: action.payload };
    case 'SET_COLD_MEDIA_FLOWRATE_ERROR':
      return { ...state, coldMediaFlowrateError: action.payload };
    case 'SET_COLD_MEDIA_TEMP_IN_ERROR':
      return { ...state, coldMediaTempInError: action.payload };
    case 'SET_COLD_MEDIA_TEMP_OUT_ERROR':
      return { ...state, coldMediaTempOutError: action.payload };
    case 'SET_COLD_MEDIA_PRESSURE_DROP_ERROR':
      return { ...state, coldMediaPressureDropError: action.payload };
    case 'SET_HOT_MEDIA_PRESSURE_DROP_ERROR':
      return { ...state, hotMediaPressureDropError: action.payload };
    case 'SET_PHE_TYPES':
      return { ...state, pheTypes: action.payload };
    case 'SET_FLOW_UNIT':
      return { ...state, flowUnit: action.payload };
    case 'SET_READ_ONLY_FIELDS':
      // Merge new read-only fields with existing ones to support both single-section and multi-section
      const existingReadOnlyFields = state.readOnlyfields || [];
      const mergedReadOnlyFields = Array.from(new Set([...existingReadOnlyFields, ...action.payload]));
      return { ...state, readOnlyfields: mergedReadOnlyFields };
    case 'SET_SHOW_WARNING_DIALOG':
      return { ...state, showWarningDialog: action.payload };
    case 'SET_SHOW_INFORMATION':
      return { ...state, showInformation: action.payload };
    case 'SET_INPUT_VALUES':
      return { ...state, inputValues: action.payload };
    case 'RESET_ERRORS':
      return {
        ...state,
        hotMediaFlowrateError: '',
        hotMediaTempInError: '',
        hotMediaTempOutError: '',
        temperatureError: [],
        coldMediaFlowrateError: '',
        coldMediaTempInError: '',
        coldMediaTempOutError: '',
        coldMediaPressureDropError: '',
        hotMediaPressureDropError: ''
      };
    case 'RESET_FORM':
      return { ...initialState };
    case 'RESET_FORM_VALUES':
      return {
        ...state,
        // Reset configuration data to force re-render with loading state
        unitConfiguration: undefined,
        rangeValidations: undefined,
        userInputs: undefined,
        // Reset all media input values
        coldMediaFlowrate: undefined,
        coldMediaTempIn: undefined,
        coldMediaTempOut: undefined,
        coldMediaPressureDrop: undefined,
        hotMediaFlowrate: undefined,
        hotMediaTempIn: undefined,
        hotMediaTempOut: undefined,
        hotMediaPressureDrop: undefined,
        // Reset read-only fields
        readOnlyfields: [],
        // Reset input values object
        inputValues: {},
        // Reset all error states
        hotMediaFlowrateError: '',
        hotMediaTempInError: '',
        hotMediaTempOutError: '',
        temperatureError: [],
        coldMediaFlowrateError: '',
        coldMediaTempInError: '',
        coldMediaTempOutError: '',
        coldMediaPressureDropError: '',
        hotMediaPressureDropError: '',
        // Reset calculation results
        responseReceived: false,
        designSession: [],
        submitText: 'Calculate',
        haveColdMediaOutputs: false,
        haveHotMediaOutputs: false,
        designReceived: false,
        warningMessage: undefined,
        showWarningForPHE: false,
        disableCopyButton: false,
        showAdditionalSteps: false,
        isXmlUsable: false,
        showXmlMessage: false,
        xmlContentReady: false
      };
    default:
      return state;
  }
}; 